import { DEFAULT_API_KEY_NAME, type GenerateApiKeyError, generateApiKeyUseCase } from "@domain/api-keys"
import { OutboxEventWriter } from "@domain/events"
import {
  type CreateProjectError,
  createProject,
  createProjectUseCase,
  InvalidProjectNameError,
  ProjectRepository,
} from "@domain/projects"
import {
  type ConcurrentSqlTransactionError,
  generateSlug,
  type OrganizationId,
  type RepositoryError,
  SqlClient,
  toRepositoryError,
} from "@domain/shared"
import { Effect } from "effect"

export interface ProvisionOrganizationWorkspaceInput {
  readonly organizationId: OrganizationId
  readonly actorUserId: string
  readonly name: string
  readonly slug: string
  readonly defaultProjectName: string
}

const DEFAULT_SAMPLE_PROJECT_NAME = "Sample project"

export interface ProvisionOrganizationWorkspaceResult {
  readonly defaultApiKey: {
    readonly id: string
    readonly name: string
    readonly token: string
  }
  readonly defaultProject: {
    readonly id: string
    readonly name: string
    readonly slug: string
  }
  readonly sampleProject: {
    readonly id: string
    readonly name: string
    readonly slug: string
  }
}

export type ProvisionOrganizationWorkspaceError =
  | RepositoryError
  | ConcurrentSqlTransactionError
  | GenerateApiKeyError
  | CreateProjectError

export const provisionOrganizationWorkspaceUseCase = Effect.fn("organizations.provisionOrganizationWorkspace")(
  function* (input: ProvisionOrganizationWorkspaceInput) {
    const sqlClient = yield* SqlClient

    yield* Effect.annotateCurrentSpan("organization.id", input.organizationId)

    return yield* sqlClient.transaction(
      Effect.gen(function* () {
        const outboxEventWriter = yield* OutboxEventWriter

        const defaultApiKey = yield* generateApiKeyUseCase({
          name: DEFAULT_API_KEY_NAME,
          isSandbox: false,
          actorUserId: input.actorUserId,
        })

        const defaultProject = yield* createProjectUseCase({
          name: input.defaultProjectName,
          actorUserId: input.actorUserId,
        })

        const projectRepo = yield* ProjectRepository
        const sampleProjectSlug = yield* generateSlug({
          name: DEFAULT_SAMPLE_PROJECT_NAME,
          count: (slug) => projectRepo.countBySlug(slug),
        }).pipe(
          Effect.catchTag("InvalidSlugInputError", (error) =>
            Effect.fail(new InvalidProjectNameError({ field: DEFAULT_SAMPLE_PROJECT_NAME, message: error.reason })),
          ),
        )
        const sampleProject = createProject({
          organizationId: input.organizationId,
          name: DEFAULT_SAMPLE_PROJECT_NAME,
          slug: sampleProjectSlug,
          settings: { isSample: true },
        })
        // Skip createProjectUseCase so sample data seeding is the only side effect for this project.
        yield* projectRepo.save(sampleProject)

        yield* outboxEventWriter
          .write({
            eventName: "OrganizationCreated",
            aggregateType: "organization",
            aggregateId: input.organizationId,
            organizationId: input.organizationId,
            payload: {
              organizationId: input.organizationId,
              actorUserId: input.actorUserId,
              name: input.name,
              slug: input.slug,
            },
          })
          .pipe(Effect.mapError((error) => toRepositoryError(error, "write")))

        yield* outboxEventWriter
          .write({
            eventName: "SampleProjectCreated",
            aggregateType: "project",
            aggregateId: sampleProject.id,
            organizationId: input.organizationId,
            payload: {
              organizationId: input.organizationId,
              projectId: sampleProject.id,
              queueAssigneeUserIds: [input.actorUserId],
              apiKeyId: defaultApiKey.id,
              timelineAnchorIso: new Date().toISOString(),
            },
          })
          .pipe(Effect.mapError((error) => toRepositoryError(error, "write")))

        return {
          defaultApiKey: {
            id: defaultApiKey.id,
            name: defaultApiKey.name,
            token: defaultApiKey.token,
          },
          defaultProject: {
            id: defaultProject.id,
            name: defaultProject.name,
            slug: defaultProject.slug,
          },
          sampleProject: {
            id: sampleProject.id,
            name: sampleProject.name,
            slug: sampleProject.slug,
          },
        }
      }),
    )
  },
)
